"use client";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  addEmployee,
  AddEmployeeSchema,
  AddEmployeeSchemaType,
} from "@/services/addEmployee";
import { format } from "date-fns";
import { ArrowLeft, CalendarIcon, ChevronLeft } from "lucide-react";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { countries } from "@/data/countries";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, setDateToNoon } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";

export default function AddEmployee() {
  const { user } = useAuth();

  const addEmployeeForm = useForm<AddEmployeeSchemaType>({
    resolver: zodResolver(AddEmployeeSchema),
    defaultValues: {
      name: "",
      email: "",
      role: "employee",
      company: user?.company?._id || "",
      gender: "",
      // dateOfBirth: "",
      idNumber: "",
      nationality: "",
      phoneNumber: "",
      address: "",
      // photo: "",
    },
  });
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const handleAddEmployeeMutation = useMutation({
    mutationFn: async (formData: AddEmployeeSchemaType) =>
      addEmployee(formData),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      toast.success("Employee added successfully");
      navigate("/app/employees");
    },
    onError: (error) => {
      const newError = error as any;
      console.error(
        "An error occurred while adding the employee:",
        newError.response?.data?.message
      );
      toast.error(newError.response?.data?.message ?? "An error occured");
    },
  });

  const { isPending } = handleAddEmployeeMutation;

  const onSubmit = (values: AddEmployeeSchemaType) => {
    handleAddEmployeeMutation.mutate(values);
  };

  return (
    <div className="w-full  md:p-6 min-h-screen">
      {/* <Link
        type="button"
        to="/app/employees"
        className={`${buttonVariants({ variant: "outline" })}`}
      >
        <ChevronLeft className="h-4 w-4" />
      </Link> */}
      <div className="flex items-center gap-4 mb-4">
        <Link
          to="/app/employees"
          className={`${buttonVariants({
            variant: "ghost",
          })}  flex items-center gap-2`}
        >
          {/* <ChevronLeft className="h-4 w-4" />
           */}
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="items-center gap-4">
          <h1 className="text-xl font-medium">Add Employee</h1>
          <p className="text-gray-500">
            Fill in the form to add a new employee
          </p>
        </div>
      </div>
      <div className="mb-4 max-w-4xl mx-auto">
        <Form {...addEmployeeForm}>
          <form onSubmit={addEmployeeForm.handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-6 p-6 border rounded-md">
              <FormField
                control={addEmployeeForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Employee Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={addEmployeeForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={addEmployeeForm.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender </FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Gender" />
                          </SelectTrigger>
                        </FormControl>

                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* <FormField
                control={addEmployeeForm.control}
                name="dateOfBirth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date of Birth</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
              <FormField
                control={addEmployeeForm.control}
                name="dateOfBirth"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Date of birth</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-[240px] pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={setDateToNoon(field.value)}
                          onSelect={(date) =>
                            field.onChange(setDateToNoon(date))
                          }
                          disabled={(date) => {
                            const todayNoon = setDateToNoon(new Date());
                            const minDateNoon = setDateToNoon(
                              new Date("1900-01-01")
                            );
                            return date > todayNoon || date < minDateNoon;
                          }}
                          captionLayout="dropdown"
                        />
                      </PopoverContent>
                    </Popover>
                    {/* <FormDescription>
                      Your date of birth is used to calculate your age.
                    </FormDescription> */}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={addEmployeeForm.control}
                name="idNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ID Number</FormLabel>
                    <FormControl>
                      <Input placeholder="ID Number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={addEmployeeForm.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Phone Number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={addEmployeeForm.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input placeholder="Address" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* countries dropdown  */}
              <FormField
                control={addEmployeeForm.control}
                name="nationality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nationality</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Nationality" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>Countries</SelectLabel>
                            {countries.map((country) => (
                              <SelectItem key={country.id} value={country.name}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* <FormField
                control={addEmployeeForm.control}
                name="nationality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nationality</FormLabel>
                    <FormControl>
                      <Input placeholder="Nationality" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
              {/* <FormField
                control={addEmployeeForm.control}
                name="photo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Photo</FormLabel>
                    <FormControl>
                      <Input type="file" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
              <div className="flex justify-end">
                <Button type="submit" className="py-2 rounded-md">
                  {isPending ? "Adding employee..." : "Add Employee"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
